import {
  Button,
  CheckboxLabel,
  IconNode,
  Loader,
  Modal,
  ToolTip,
  Typography
} from '@components/base';
import css from './index.module.scss';
import Images from '@assets/images';
import { InputDatePicker } from '@components/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import notify from '@helpers/toastify-helper';
import { fetchQuoteTableAdhocOptions, submitGeneratePDF } from '@services/quote.service';
import { openDocumentSignedLink } from '@helpers/utils';
import moment from 'moment';
import CustomisePDF from './components/customize-document';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import Divider from '@components/base/divider';
import { useNavigate } from 'react-router-dom';

export interface IFieldOptions {
  key: string;
  label: string;
  default?: boolean;
}

export interface IForm {
  validityDate: Date | null;
  roundedValues: boolean;
  twoDecimalPlaces: boolean;
  tableFields: IFieldOptions[];
  adhocFields: IFieldOptions[];
}
export interface IGeneratePDFState {
  customizeDocument: boolean;
  previewLink: string;
  tableOptions: IFieldOptions[];
  adhocOptions: IFieldOptions[];
  fixedTableOptions: IFieldOptions[];
}

const DISPLAY_FORMAT = {
  ROUND_UP: 1,
  TWO_DECIMAL_PLACES: 2,
  DEFAULT: 0
};

const INITIAL_PDF_STATE: IGeneratePDFState = {
  customizeDocument: false,
  previewLink: '',
  tableOptions: [],
  adhocOptions: [],
  fixedTableOptions: []
};

interface IQuote {
  quote_id: string;
  // Add other properties as needed
}

interface IReqBody {
  last_valid_date: string | null;
  is_preview: boolean;
  quotes: { quote_id: string }[];
  display_format: number;
  table_data?: { key: string; label: string; index?: number }[];
  adhoc_data?: { key: string; label: string }[];
}

export interface IGeneratePDFProps {
  open: boolean;
  onClose: () => void;
  date: Date | null;
  isConsolidation?: boolean;
  enquiryId: string;
  selectedQuotes: IQuote[];
  setQuoteDetail: (quoteDetail: any) => void;
  onSubmitClick: () => void;
  marketType: string;
}

const GeneratePDF = (props: IGeneratePDFProps) => {
  const {
    open,
    onClose,
    date,
    isConsolidation,
    enquiryId,
    selectedQuotes,
    setQuoteDetail,
    onSubmitClick,
    marketType
  } = props;
  const navigate = useNavigate();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [pdfState, setPdfState] = useState<IGeneratePDFState>(INITIAL_PDF_STATE);
  const { customizeDocument, previewLink } = pdfState;

  const form = useForm<IForm>({
    defaultValues: {
      validityDate: date,
      roundedValues: false,
      twoDecimalPlaces: false,
      tableFields: [],
      adhocFields: []
    }
  });
  const {
    setValue,
    watch,
    control,
    handleSubmit,
    getValues,
    formState: { isSubmitting }
  } = form;

  const tomorrow = useMemo(() => {
    const date = new Date();
    // If you want to disallow selecting today's date, set `date.getDate()` => `date.getDate() + 1`
    date.setDate(date.getDate());
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const getTableAdhocOptions = async () => {
    const queryString = new URLSearchParams({
      market_type: marketType
    }).toString();
    const response = await fetchQuoteTableAdhocOptions(queryString);

    if (response.success) {
      const { data } = response;

      setPdfState((prev) => {
        const allTableOptions = [
          ...(data?.table_fields?.fixed_fields || []),
          ...(data?.table_fields?.optional_fields || [])
        ];
        return {
          ...prev,
          tableOptions: allTableOptions,
          adhocOptions: data?.adhoc_fields,
          fixedTableOptions: data?.table_fields.fixed_fields
        };
      });
      const defaultTableFields = [
        ...(data?.table_fields?.fixed_fields || []),
        ...(data?.table_fields?.optional_fields || []).filter(
          (field: IFieldOptions) => field.default
        )
      ];
      setValue('tableFields', defaultTableFields);

      const defaultAdhocFields = (data?.adhoc_fields || []).filter(
        (field: IFieldOptions) => field.default
      );
      setValue('adhocFields', defaultAdhocFields);
    } else {
      notify({
        severity: 'error',
        message: response?.error?.error || 'Something Went Wrong!, Contact Tech Team'
      });
    }
  };

  const toggleDatePicker = () => {
    setIsDatePickerOpen((prev) => !prev);
  };

  const handleCheckboxChange = useCallback(
    (field: 'roundedValues' | 'twoDecimalPlaces') => {
      const currentValues = getValues();
      if (field === 'roundedValues') {
        if (currentValues.roundedValues) {
          setValue('roundedValues', false);
        } else {
          setValue('roundedValues', true);
          setValue('twoDecimalPlaces', false);
        }
      } else {
        if (currentValues.twoDecimalPlaces) {
          setValue('twoDecimalPlaces', false);
        } else {
          setValue('twoDecimalPlaces', true);
          setValue('roundedValues', false);
        }
      }
    },
    [setValue, getValues]
  );

  const constructReqBody = (data: IForm, isPreview = false) => {
    const { validityDate, roundedValues, twoDecimalPlaces, tableFields, adhocFields } = data;
    const tableData = tableFields.map((field: IFieldOptions, index: number) => ({
      key: field.key,
      label: field.label,
      index: index
    }));
    const adhocData = adhocFields.map((field: IFieldOptions, index: number) => ({
      key: field.key,
      label: field.label
    }));
    const quoteData = selectedQuotes.map((quote: IQuote) => ({
      quote_id: quote.quote_id
    }));
    const reqBody: IReqBody = {
      last_valid_date: validityDate ? `${moment(validityDate).format('YYYY-MM-DD')}` : null,
      is_preview: isPreview,
      quotes: quoteData,
      display_format: roundedValues
        ? DISPLAY_FORMAT.ROUND_UP
        : twoDecimalPlaces
        ? DISPLAY_FORMAT.TWO_DECIMAL_PLACES
        : DISPLAY_FORMAT.DEFAULT
    };
    if (customizeDocument) {
      reqBody.table_data = tableData;
      reqBody.adhoc_data = adhocData;
    }
    return reqBody;
  };

  const onFormSubmit = async (data: IForm, isPreview = false) => {
    const reqBody: IReqBody = constructReqBody(data, isPreview);
    const response = await submitGeneratePDF(enquiryId, reqBody);
    if (response.success) {
      const { data: responseData } = response;
      if (isPreview) {
        setPdfState((prev) => ({
          ...prev,
          previewLink: responseData.pdf_path
        }));
        openDocumentSignedLink(responseData.pdf_path);
      } else {
        notify({
          severity: 'success',
          message: 'PDF Generated Successfully!'
        });
        openDocumentSignedLink(responseData.pdf_path);
        navigate(`/quote/detail/${enquiryId}?activeTab=documents`);
        onSubmitClick();
        onClose();
      }
    } else {
      notify({
        severity: 'error',
        message: response?.error?.error || 'Something Went Wrong!, Contact Tech Team'
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <main className={css.mainWrapper}>
        <section className={css.header}>
          <Typography variant="h4">Generate Quote PDF</Typography>
          <IconNode
            src={Images.crossBlack}
            alt="close icon"
            component="button"
            className={css.closeButton}
            onClick={onClose}
          />
        </section>

        <form
          noValidate
          onSubmit={handleSubmit((data) => onFormSubmit(data))}
          className={css.formWrapper}
        >
          <section className={css.contentWrapper}>
            <section className={`${css.content} ${isDatePickerOpen ? css.contentOpen : ''}`}>
              <Controller
                name="validityDate"
                control={control}
                render={({ field: { value } }) => {
                  return (
                    <InputDatePicker
                      label="Price Validity of Quote"
                      value={value}
                      required
                      dayDisabled={[{ before: tomorrow }]}
                      setDatePickerOpen={setIsDatePickerOpen}
                      onSelect={(day: Date | undefined) => {
                        setValue('validityDate', (day as Date) || null);
                        toggleDatePicker();
                      }}
                      rootClassName={css.fieldSpacing}
                    />
                  );
                }}
              />
              <div className={css.checkboxRow}>
                <div className={css.checkboxLabel}>
                  <Typography variant="p" className={css.formatLabel}>
                    Display Format
                  </Typography>
                  <ToolTip
                    placement="right"
                    title="By default, prices in MT will be rounded up, and for others it would be adjusted to 2
              decimal places."
                  >
                    <div>
                      <IconNode src={Images.infoGrey} alt="Info icon" />
                    </div>
                  </ToolTip>
                </div>
                <div className={css.checkboxGroup}>
                  <Controller
                    name="roundedValues"
                    control={control}
                    render={({ field: { value } }) => (
                      <CheckboxLabel
                        id="roundedValues"
                        checked={value}
                        onChange={() => handleCheckboxChange('roundedValues')}
                        label="Round up"
                      />
                    )}
                  />
                  <Controller
                    name="twoDecimalPlaces"
                    control={control}
                    render={({ field: { value } }) => (
                      <CheckboxLabel
                        id="twoDecimalPlaces"
                        checked={value}
                        onChange={() => handleCheckboxChange('twoDecimalPlaces')}
                        label="Two Decimal Places"
                      />
                    )}
                  />
                </div>
              </div>
            </section>

            <Divider className={css.divider} />

            <Button
              variant="text"
              className={css.customizeDocumentButton}
              onClick={() => {
                setPdfState((prev) => ({
                  ...prev,
                  customizeDocument: !prev.customizeDocument
                }));
              }}
            >
              <div className={css.customizeDocumentButtonContent}>
                Customize Document
                <IconNode
                  src={customizeDocument ? Images.arrowUpBig : Images.arrowDownBig}
                  alt="Arrow Icon"
                />
              </div>
            </Button>

            {customizeDocument && (
              <FormProvider {...form}>
                <div className={css.customizeDocumentWrapper}>
                  <CustomisePDF
                    pdfState={pdfState}
                    setPdfState={setPdfState}
                    getTableAdhocOptions={getTableAdhocOptions}
                  />
                  <div className={css.previewButtonWrapper}>
                    <Button
                      variant="text"
                      onClick={handleSubmit((data) => onFormSubmit(data, true))}
                      disabled={isSubmitting}
                    >
                      Preview PDF
                    </Button>
                    {previewLink && (
                      <IconNode
                        src={Images.colouredPdf}
                        alt="Preview PDF"
                        onClick={() => {
                          openDocumentSignedLink(previewLink);
                        }}
                      />
                    )}
                  </div>
                </div>
              </FormProvider>
            )}
          </section>
          <section className={css.modalFooter}>
            <Button
              variant="contained"
              className={css.generateButton}
              type="submit"
              disabled={isSubmitting}
            >
              Generate PDF
            </Button>
          </section>
          {isSubmitting ? <Loader open={isSubmitting} /> : <> </>}
        </form>
      </main>
    </Modal>
  );
};

export default GeneratePDF;
